import {
  fetchTransferRecommendations,
  toTransferRecommendationsError,
  TransferRecommendationsApiError,
  type TransferRecommendationQuery,
  type TransferRecommendationResponse
} from './recommendations';
import { toTransferRunRequestBody } from './transfer-run-request-body';
import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import {
  openTransferRunDoorbell,
  type TransferRunDoorbell
} from '@lib/realtime/transfer-run-doorbell';

export type TransferRunAcceptedResponse =
  components['schemas']['TransferRunAcceptedResponse'];
export type TransferRunAcknowledgement = TransferRunAcceptedResponse['data'];

export type RequestTransferRecommendationsOptions =
  TransferRecommendationQuery & {
    accessToken: string;
    signal?: AbortSignal;
  };

const CONFLICT_STATUS = 409;
const SERVICE_UNAVAILABLE_STATUS = 503;
const GATEWAY_TIMEOUT_STATUS = 504;

/**
 * How many times a run that cannot hand over its answer is started again. The
 * backend answers 409 when the finished answer is filed under another solver
 * build, and says asking again finds it, so one further attempt is expected to
 * be enough. The cap exists so a backend that kept answering 409 would fail
 * rather than loop.
 */
const MAX_RESTART_ATTEMPTS = 3;

/**
 * How long the client waits for a run before giving up on it. The backend
 * abandons a run at ten minutes and reports 504, so this only bites when no
 * answer arrives at all. Two minutes is many times the measured solve time and
 * keeps a wedged run from holding the member on a working state indefinitely.
 */
const MAX_RUN_WAIT_MS = 2 * 60 * 1000;

/**
 * How many of the backend's own retry intervals to wait for an announcement
 * before asking anyway.
 *
 * Expressed in the interval the backend states rather than as a duration of its
 * own, so the one number governing how often to ask still governs it. Long
 * enough that a solve running well past its p99 is still announced rather than
 * polled, short enough that a member whose announcement never arrives waits
 * once and then falls back to asking for the rest of the run.
 */
const DOORBELL_PATIENCE_INTERVALS = 15;

const RUN_TIMED_OUT_MESSAGE =
  'Recommendations took too long to produce. Try again.';

const RUN_UNCOLLECTABLE_MESSAGE =
  'Recommendations could not be collected. Try again.';

type RecommendationsOutcome = {
  status: 'recommendations';
  recommendations: TransferRecommendationResponse;
};

type StartRunOutcome =
  | RecommendationsOutcome
  | { status: 'accepted'; run: TransferRunAcknowledgement }
  /** The work could not be queued, so it must be solved synchronously. */
  | { status: 'unavailable' };

type ReadRunOutcome =
  | RecommendationsOutcome
  | { status: 'working'; run: TransferRunAcknowledgement }
  /** Finished, but not collectable under this run. Starting a new one finds it. */
  | { status: 'restart' };

/**
 * Both endpoints answer 200 with recommendations and 202 with an
 * acknowledgement, so a successful body is read by shape rather than by status.
 */
const isAcknowledgement = (
  body: TransferRecommendationResponse | TransferRunAcceptedResponse
): body is TransferRunAcceptedResponse => 'runId' in body.data;

/**
 * Why a wait ended early, as something throwable.
 *
 * `AbortSignal.reason` carries the cancellation where the environment supplies
 * it, but not every one does, and rejecting with whatever it holds means
 * rejecting with `undefined` on those. An abort is the one rejection here that
 * is never anybody's fault, so it must still arrive as an error rather than as
 * nothing at all.
 */
const ABANDONED_MESSAGE = 'The transfer recommendations were no longer needed';

const toAbortReason = (signal?: AbortSignal): unknown =>
  signal?.reason ?? new Error(ABANDONED_MESSAGE);

const wait = (milliseconds: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(toAbortReason(signal));
      return;
    }
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, milliseconds);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(toAbortReason(signal));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Asks for recommendations without waiting for the solve. Returns them outright
 * when the answer already exists, otherwise the run to come back for.
 */
export const startTransferRecommendationRun = async ({
  accessToken,
  signal,
  ...query
}: RequestTransferRecommendationsOptions): Promise<StartRunOutcome> => {
  const { data, error, response } = await ffhClient().POST(
    '/users/me/recommendations/transfers/runs',
    {
      headers: authHeader(accessToken),
      // A body rather than the synchronous endpoint's query string, so the four
      // list parameters travel as lists. The encoding is the only difference;
      // see `toTransferRunRequestBody` for why it must stay that way.
      //
      // Cast because the generated type marks every defaulted field required,
      // which is true of the parsed request and not of the one sent: the spec
      // lists none of them as required, so `{}` is a valid body even though the
      // body itself must be present. Supplying them to satisfy the type would
      // mean keeping a copy of the backend's defaults here, which is exactly
      // the drift that quietly computes a second cache key for the same
      // request.
      body: toTransferRunRequestBody(
        query
      ) as components['schemas']['TransferRunRequest'],
      signal
    }
  );

  // Not a failure: the work was refused rather than degraded, and every reason
  // it is refused for is recoverable by solving on the request path instead.
  if (response?.status === SERVICE_UNAVAILABLE_STATUS) {
    return { status: 'unavailable' };
  }

  if (error || !data) {
    throw toTransferRecommendationsError(response?.status ?? 0, error);
  }

  return isAcknowledgement(data)
    ? { status: 'accepted', run: data.data }
    : { status: 'recommendations', recommendations: data };
};

/**
 * Collects the work a run started. Addressed by the run rather than by the
 * request on purpose: a solve is keyed on every price and prediction that went
 * into it, so asking the same question again would name different work.
 */
export const readTransferRecommendationRun = async ({
  accessToken,
  runId,
  signal
}: {
  accessToken: string;
  runId: string;
  signal?: AbortSignal;
}): Promise<ReadRunOutcome> => {
  const { data, error, response } = await ffhClient().GET(
    '/users/me/recommendations/transfers/runs/{runId}',
    {
      headers: authHeader(accessToken),
      params: { path: { runId } },
      signal
    }
  );

  // The answer exists, just not under this run. Both codes behind this status
  // mean ask again rather than anything the member needs telling about.
  if (response?.status === CONFLICT_STATUS) {
    return { status: 'restart' };
  }

  if (error || !data) {
    throw toTransferRecommendationsError(response?.status ?? 0, error);
  }

  return isAcknowledgement(data)
    ? { status: 'working', run: data.data }
    : { status: 'recommendations', recommendations: data };
};

const collectRun = async ({
  accessToken,
  run,
  doorbell,
  signal
}: {
  accessToken: string;
  run: TransferRunAcknowledgement;
  doorbell: TransferRunDoorbell | undefined;
  signal?: AbortSignal;
}): Promise<RecommendationsOutcome | { status: 'restart' }> => {
  const giveUpAt = Date.now() + MAX_RUN_WAIT_MS;
  let retryAfterMilliseconds = run.retryAfterMilliseconds;
  // Whether this member still looks reachable by announcement. A patient wait
  // that ends having heard nothing is the evidence they are not: publishing is
  // best-effort and silent about its own failures, so nothing else will ever
  // say so. Staying patient after that would make a member who cannot hear the
  // doorbell wait longer for every collection than one who never had it.
  let isAnnouncementExpected = doorbell !== undefined;

  while (Date.now() < giveUpAt) {
    // Never wait past the point this gives up. Both waits below can be longer
    // than what is left, and `retryAfterMilliseconds` carries no upper bound in
    // the contract, so an unclamped wait would overrun the deadline and then
    // still collect, making the limit above a suggestion rather than a limit.
    const remainingMilliseconds = giveUpAt - Date.now();

    if (doorbell) {
      // Wait to be told rather than asking repeatedly. Every wait that ends in
      // an announcement is four or so requests this member did not make, which
      // at the deadline is the whole point of being told at all. Once nothing
      // has arrived, the wait drops to the interval the backend states: the
      // doorbell stays open and still cuts the wait short if it rings, but it
      // no longer decides how long to wait.
      const patienceMilliseconds =
        retryAfterMilliseconds *
        (isAnnouncementExpected ? DOORBELL_PATIENCE_INTERVALS : 1);

      const wasAnnounced = await doorbell.waitForRun(
        run.runId,
        Math.min(patienceMilliseconds, remainingMilliseconds),
        signal
      );

      if (!wasAnnounced) {
        isAnnouncementExpected = false;
      }

      // A wait abandoned mid-announcement reports itself as nothing heard,
      // because not hearing is all it can observe. Left at that, the one case
      // that must stop the loop would instead fall through and collect a run
      // nobody is waiting for. The other wait rejects on abort and never
      // reaches here, so this is where the two agree.
      if (signal?.aborted) {
        throw toAbortReason(signal);
      }
    } else {
      // The backend states how long to leave it, so the interval is never
      // invented here.
      await wait(
        Math.min(retryAfterMilliseconds, remainingMilliseconds),
        signal
      );
    }

    const outcome = await readTransferRecommendationRun({
      accessToken,
      runId: run.runId,
      signal
    });

    if (outcome.status !== 'working') {
      return outcome;
    }

    retryAfterMilliseconds = outcome.run.retryAfterMilliseconds;
  }

  throw new TransferRecommendationsApiError(
    GATEWAY_TIMEOUT_STATUS,
    RUN_TIMED_OUT_MESSAGE
  );
};

/**
 * Transfer recommendations, however they have to be obtained.
 *
 * Resolves only once there is an answer, so a caller's in-flight state covers
 * the whole wait rather than just the request that started it. Refusals and
 * failures reject exactly as the synchronous endpoint's do.
 */
export const requestTransferRecommendations = async ({
  accessToken,
  signal,
  ...query
}: RequestTransferRecommendationsOptions): Promise<TransferRecommendationResponse> => {
  for (let attempt = 0; attempt < MAX_RESTART_ATTEMPTS; attempt += 1) {
    const started = await startTransferRecommendationRun({
      accessToken,
      signal,
      ...query
    });

    if (started.status === 'unavailable') {
      return fetchTransferRecommendations({ accessToken, signal, ...query });
    }

    if (started.status === 'recommendations') {
      return started.recommendations;
    }

    // Named only when the backend can reach this member that way. Its absence
    // is the instruction to ask, which is also what every failure to listen
    // falls back to, so nothing here can cost the member their answer.
    const doorbell = started.run.channel
      ? await openTransferRunDoorbell({
          channel: started.run.channel,
          accessToken
        })
      : undefined;

    try {
      const collected = await collectRun({
        accessToken,
        run: started.run,
        doorbell,
        signal
      });

      if (collected.status === 'recommendations') {
        return collected.recommendations;
      }
    } finally {
      doorbell?.close();
    }
  }

  throw new TransferRecommendationsApiError(
    CONFLICT_STATUS,
    RUN_UNCOLLECTABLE_MESSAGE
  );
};
